import type { JSX } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

import { renderTextInput } from './form-input-renders';
import { InputGroup } from './inputgroup';

export interface FieldRendererProps<T> {
    hasError: boolean;
    placeholder: string;
    label: string;
    value: T;
    onChange: (e: any) => void;
    onBlur: (e: any) => void;
    disabled: boolean;
    error?: string;
    name: string;
    extraProps?: any;
    loading?: boolean;
}

export interface FieldRenderer<T = number | string> {
    (props: FieldRendererProps<T>, ref: any): JSX.Element;
}

export interface FieldRendererWithAside<T = number | string> {
    (
        props: FieldRendererProps<T>,
        ref: any
    ): {
        input: JSX.Element;
        aside: JSX.Element;
        reversed?: boolean;
    };
}

export function FormController<T extends FieldValues, ValueT = number | string>({
    control,
    label,
    field,
    placeholder,
    plainLabel,
    asLabel = true,
    required = true,
    renderInput,
    className,
    wrapperClassName,
    labelClassName,
    errorClassName,
    extraProps,
    loading,
    mapValue,
}: {
    control: Control<T>;
    field: FieldPath<T>;
    label: string;
    placeholder?: string;

    asLabel?: boolean;

    plainLabel?: boolean;

    required?: boolean;

    className?: string;
    wrapperClassName?: string;
    labelClassName?: string;
    errorClassName?: string;

    loading?: boolean;

    extraProps?: any;

    renderInput?: FieldRenderer<ValueT> | FieldRendererWithAside<ValueT>;
    mapValue?: (value: any) => any;
}) {
    const thePlaceholder = placeholder ?? label;

    const theLabel = plainLabel ? label : `${label}${required ? '*' : ''}`;

    const renderer = renderInput ?? renderTextInput;

    return (
        <Controller
            name={field}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const renderResult = renderer(
                    {
                        hasError: !!error,
                        placeholder: thePlaceholder,
                        label: theLabel,
                        value: mapValue ? mapValue(field.value) : field.value,
                        onChange: field.onChange,
                        onBlur: field.onBlur,
                        disabled: !!field.disabled,
                        error: error?.message,
                        name: field.name,
                        extraProps,
                        loading,
                    },
                    field.ref
                );

                let node;
                let aside;
                let hasAside = false;
                let reversed = false;

                if (isFieldRendererResultWithAside(renderResult)) {
                    node = renderResult.input;
                    hasAside = true;
                    aside = renderResult.aside;
                    reversed = renderResult.reversed ?? false;
                } else {
                    node = renderResult;
                }

                const inputGroup = (
                    <InputGroup
                        label={theLabel}
                        hasError={!!error}
                        errorMessage={error?.message ?? ''}
                        gapSize="md"
                        asLabel={asLabel}
                        wrapperClassName={wrapperClassName}
                        labelClassName={labelClassName}
                        errorClassName={errorClassName}
                        className={className}
                    >
                        {node}
                    </InputGroup>
                );

                if (hasAside) {
                    return (
                        <div className="flex gap-4 w-full">
                            {reversed ? aside : inputGroup}
                            {reversed ? inputGroup : aside}
                        </div>
                    );
                }

                return inputGroup;
            }}
        />
    );
}

const isFieldRendererResultWithAside = (
    value: any
): value is {
    input: JSX.Element;
    aside: JSX.Element;
    reversed?: boolean;
} => {
    return typeof value === 'object' && value !== null && 'input' in value && 'aside' in value;
};
